import {
  Flex,
  useBreakpointValue,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import NestedTabList from 'src/layouts/NestedTabList';
import PeerReviewsTab from './PeerReviewsTab';
import AdjudicationsTab from './AdjudicationsTab';
import EarlyAssessmentsTab from './EarlyAssessmentsTab';
import { useSubmissionDetails } from 'src/pages/SubmissionDetails/SubmissionDetailsContext';
import { useSubmissionContextData } from 'src/contexts/SubmissionContextProvider';
import { IRoleType } from '@publishing-engine/domain/models';

export const REVIEWS_TABS = {
  PEER_REVIEWS: 'Peer Reviews',
  ADJUDICATIONS: 'Adjudications',
  EARLY_ASSESSMENTS: 'Early Assessments'
} as const;

export type SubmissionDetailReviewTab =
  (typeof REVIEWS_TABS)[keyof typeof REVIEWS_TABS];

type RenderTabContent = {
  activeTab: SubmissionDetailReviewTab;
  roleContext: IRoleType;
  setReviewsSubTab: (tab: SubmissionDetailReviewTab) => void;
};

export const renderTabContent = ({
  activeTab,
  roleContext,
  setReviewsSubTab
}: RenderTabContent) => {
  switch (activeTab) {
    case REVIEWS_TABS.PEER_REVIEWS:
      return <PeerReviewsTab key={activeTab} roleContext={roleContext} />;
    case REVIEWS_TABS.ADJUDICATIONS:
      return (
        <AdjudicationsTab key={activeTab} setReviewsSubTab={setReviewsSubTab} />
      );
    case REVIEWS_TABS.EARLY_ASSESSMENTS:
      return <EarlyAssessmentsTab key={activeTab} />;
    default:
      return null;
  }
};

type Props = {
  roleContext: IRoleType;
};

const BoardMemberReviewsTab = ({ roleContext }: Props) => {
  const { reviewsSubTab, setReviewsSubTab } = useSubmissionDetails();
  const { submissionContext } = useSubmissionContextData();
  const isMobile = useBreakpointValue({ base: true, md: false });

  const hiddenConditions: Partial<Record<SubmissionDetailReviewTab, boolean>> =
    {
      [REVIEWS_TABS.ADJUDICATIONS]:
        !submissionContext?.config.isAdjudicationEnabled,
      [REVIEWS_TABS.EARLY_ASSESSMENTS]:
        !submissionContext?.config.isEarlyAssessmentEnabled
    };

  const visibleTabs = Object.values(REVIEWS_TABS).filter(
    tab => !hiddenConditions[tab]
  );

  return (
    <Flex
      flexDirection="column"
      alignItems="flex-start"
      gap={2}
      marginBottom={8}
    >
      {visibleTabs.length <= 1 ? (
        <PeerReviewsTab roleContext={roleContext} />
      ) : (
        <NestedTabList>
          <NestedTabList.Navigation>
            {isMobile ? (
              <Menu>
                <MenuButton
                  as={Button}
                  variant="ghost"
                  rightIcon={<FontAwesomeIcon icon={faChevronDown} />}
                  fontWeight="semibold"
                  size="sm"
                  px={4}
                >
                  {reviewsSubTab}
                </MenuButton>
                <MenuList zIndex={10}>
                  {visibleTabs.map(tab => (
                    <MenuItem
                      key={tab}
                      onClick={() => setReviewsSubTab(tab)}
                      fontWeight={reviewsSubTab === tab ? 'semibold' : 'normal'}
                      bg={reviewsSubTab === tab ? 'blue.50' : undefined}
                    >
                      {tab}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            ) : (
              visibleTabs.map(tab => (
                <NestedTabList.Tab
                  key={tab}
                  onClick={() => setReviewsSubTab(tab)}
                  isActive={reviewsSubTab === tab}
                >
                  {tab}
                </NestedTabList.Tab>
              ))
            )}
          </NestedTabList.Navigation>
          {renderTabContent({
            activeTab: reviewsSubTab,
            roleContext,
            setReviewsSubTab: setReviewsSubTab
          })}
        </NestedTabList>
      )}
    </Flex>
  );
};

export default BoardMemberReviewsTab;
